import { captureException } from "@sentry/react";
import {
  captureOwnerStack,
  Component,
  type ErrorInfo,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
} from "react";

import type { ErrorDetailResponse } from "@/api/bale.zod";

export type ErrorFallbackProps = {
  error: unknown;
  errorDetail?: ErrorDetailResponse | null;
  errorInfo?: ErrorInfo | null;
  ownerStack?: string | null;
};

type Props = PropsWithChildren<{
  fallback?: (props: ErrorFallbackProps) => ReactElement;
}>;

type State = {
  error: unknown;
  errorInfo: ErrorInfo | null;
  ownerStack: string | null;
};

function isErrorDetailResponse(detail: unknown): detail is ErrorDetailResponse {
  return (
    !!detail &&
    typeof detail === "object" &&
    "code" in detail &&
    typeof detail.code === "string" &&
    "message" in detail &&
    typeof detail.message === "string"
  );
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null, errorInfo: null, ownerStack: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    captureException(error);
    this.setState({ error, errorInfo, ownerStack: captureOwnerStack && captureOwnerStack() });
  }

  render(): ReactNode {
    if (this.state.error) {
      let errorDetail: ErrorDetailResponse | null = null;
      if (
        this.state.error instanceof Error &&
        "info" in this.state.error &&
        isErrorDetailResponse(this.state.error.info)
      ) {
        errorDetail = this.state.error.info;
      } else if (isErrorDetailResponse(this.state.error)) {
        errorDetail = this.state.error;
      }

      const Fallback = this.props.fallback;
      if (!Fallback) return null;

      return (
        <Fallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          ownerStack={this.state.ownerStack}
          errorDetail={errorDetail}
        />
      );
    }

    return this.props.children;
  }
}
